import type { LoyaltyCard, Product, PurchaseHint, Store } from '@recipes/contracts';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useFeature } from '@/features/shared/hooks';
import { useAuth } from '@/lib/auth/auth-provider';
import { offlineDb, type PendingCatalogAction } from '@/lib/offline/db';
import { replayCatalogQueue } from './catalog-queue';
import { catalogSyncState, startCatalogSync, type CatalogSyncState } from './device-copy';

/** Starts the device-copy sync and the replay of queued changes once a user is signed in. */
export function useCatalogBoot(): void {
  const { status } = useAuth();
  const enabled = useFeature('ingredient_catalog');
  useEffect(() => {
    if (status !== 'authenticated' || !enabled) return;
    startCatalogSync(() => void replayCatalogQueue());
  }, [status, enabled]);
}

/** Stores from the device copy – available without a connection. */
export function useCatalogStores(): Store[] {
  const rows = useLiveQuery(() => offlineDb.catalogStores.toArray(), []);
  return useMemo(
    () => (rows ?? []).map((r) => r.store).sort((a, b) => a.name.localeCompare(b.name)),
    [rows],
  );
}

export function useCatalogHints(target: {
  ingredientId?: string | null;
  productId?: string | null;
}): PurchaseHint[] {
  const { ingredientId, productId } = target;
  const rows = useLiveQuery(
    () =>
      offlineDb.catalogHints
        .where('ingredientId')
        .equals(ingredientId ?? '-')
        .or('productId')
        .equals(productId ?? '-')
        .toArray(),
    [ingredientId, productId],
  );
  return useMemo(
    () => (rows ?? []).map((r) => r.hint).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    [rows],
  );
}

export function useCatalogProduct(id: string | null | undefined): Product | null | undefined {
  const row = useLiveQuery(
    async () => (id ? ((await offlineDb.catalogProducts.get(id)) ?? null) : null),
    [id],
  );
  return row === undefined ? undefined : (row?.product ?? null);
}

export function usePendingCatalog(): PendingCatalogAction[] {
  return useLiveQuery(() => offlineDb.pendingCatalog.orderBy('id').toArray(), []) ?? [];
}

/**
 * Whether a product captured on this device still waits for the server. `undefined` while the
 * queue is being read – callers must not treat that as "confirmed".
 */
export function useProductPending(productId: string): boolean | undefined {
  return useLiveQuery(
    async () =>
      (await offlineDb.pendingCatalog
        .where('entityId')
        .equals(productId)
        .filter((action) => action.kind === 'product-create')
        .count()) > 0,
    [productId],
  );
}

export function useCatalogSyncState(): CatalogSyncState | undefined {
  return useLiveQuery(() => catalogSyncState(), []);
}

export interface ItemPicture {
  id: string;
  name: string;
  brand: string | null;
  thumbnailUrl: string | null;
}

/**
 * The product picture to show with a list item: the product chosen for it, else the one this
 * household bought under that name last time (the learned pairing). Only products that have a
 * picture; everything from the device copy, so it works in the store without a connection.
 */
export function useItemPictures(
  items: Array<{
    id: string;
    normalizedName: string;
    product?: {
      id: string;
      name: string;
      brand: string | null;
      thumbnailUrl: string | null;
    } | null;
  }>,
): Map<string, ItemPicture> {
  const key = items.map((i) => `${i.id}:${i.product?.id ?? ''}:${i.normalizedName}`).join('|');
  const found = useLiveQuery(async () => {
    const pictures = new Map<string, ItemPicture>();
    const names = items.filter((i) => !i.product).map((i) => i.normalizedName);
    const learned = new Map<string, string>();
    if (names.length)
      for (const row of await offlineDb.shoppingHistory
        .where('normalizedName')
        .anyOf(names)
        .toArray())
        if (row.entry.lastProductId) learned.set(row.normalizedName, row.entry.lastProductId);
    for (const item of items) {
      const productId = item.product?.id ?? learned.get(item.normalizedName);
      if (!productId) continue;
      const stored = (await offlineDb.catalogProducts.get(productId))?.product;
      const thumbnailUrl = stored?.photos[0]?.thumbnailUrl ?? item.product?.thumbnailUrl ?? null;
      const onDevice = (await offlineDb.catalogThumbs.get(productId)) !== undefined;
      if (!thumbnailUrl && !onDevice) continue;
      pictures.set(item.id, {
        id: productId,
        name: stored?.name ?? item.product?.name ?? '',
        brand: stored?.brand ?? item.product?.brand ?? null,
        thumbnailUrl,
      });
    }
    return pictures;
  }, [key]);
  return found ?? EMPTY_PICTURES;
}
const EMPTY_PICTURES = new Map<string, ItemPicture>();

/** Object URL of a product thumbnail stored on the device, else the signed URL (online). */
export function useProductThumb(productId: string | null | undefined, fallback?: string | null) {
  const blob = useLiveQuery(
    async () => (productId ? ((await offlineDb.catalogThumbs.get(productId))?.blob ?? null) : null),
    [productId],
  );
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);
  return url ?? fallback ?? null;
}

/** Loyalty cards from the device copy, by name. */
export function useLoyaltyCards(): LoyaltyCard[] {
  const rows = useLiveQuery(() => offlineDb.catalogLoyaltyCards.toArray(), []);
  return useMemo(
    () => (rows ?? []).map((r) => r.card).sort((a, b) => a.name.localeCompare(b.name)),
    [rows],
  );
}

/** The picture of a card: from the device when it is there, else the server's URL. */
export function useCardImage(card: LoyaltyCard | null | undefined): string | null {
  const cardId = card?.id ?? null;
  const blob = useLiveQuery(
    async () => (cardId ? ((await offlineDb.catalogCardImages.get(cardId))?.blob ?? null) : null),
    [cardId],
  );
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);
  return url ?? card?.image?.url ?? null;
}

/**
 * "Shopping at …" is a view setting of this device, not part of the shared list
 * (docs/finished/ingredient-catalog-spec.md 5.5).
 */
const SHOPPING_AT_KEY = 'catalog:shopping-at';
const shoppingAtListeners = new Set<() => void>();
const readShoppingAt = (): string | null => {
  try {
    return localStorage.getItem(SHOPPING_AT_KEY);
  } catch {
    return null;
  }
};
function writeShoppingAt(storeId: string | null): void {
  try {
    if (storeId) localStorage.setItem(SHOPPING_AT_KEY, storeId);
    else localStorage.removeItem(SHOPPING_AT_KEY);
  } catch {
    // private mode: the choice lives for this page only
  }
  shoppingAtListeners.forEach((l) => l());
}

export function useShoppingAt() {
  // One value for every component on the page (control, list rows, product card).
  const storeId = useSyncExternalStore(
    (listener) => {
      shoppingAtListeners.add(listener);
      return () => shoppingAtListeners.delete(listener);
    },
    readShoppingAt,
    () => null,
  );
  const setStoreId = writeShoppingAt;
  const stores = useCatalogStores();
  const store = useMemo(() => stores.find((s) => s.id === storeId) ?? null, [stores, storeId]);
  return { store, storeId: store ? storeId : null, setStoreId, stores };
}
